import { UmbEntityActionListElement } from '../../entity-action-list.element.js';
import type { UmbDropdownElement } from '@umbraco-cms/backoffice/components';
import { html, customElement, property, css, query, ifDefined } from '@umbraco-cms/backoffice/external/lit';
import type { UUIInterfaceLook } from '@umbraco-cms/backoffice/external/uui';
import { UmbLitElement } from '@umbraco-cms/backoffice/lit-element';

@customElement('umb-entity-actions-dropdown')
export class UmbEntityActionsDropdownElement extends UmbLitElement {
	@property({ type: Boolean })
	compact = false;

	@property({ type: String })
	public look?: UUIInterfaceLook;

	@property({ type: String })
	public label?: string;

	@query('#action-modal')
	private _dropdownElement?: UmbDropdownElement;

	#entityActionListElement?: UmbEntityActionListElement;

	// Event handler as arrow function to maintain consistent reference
	#onActionExecuted = () => {
		this._dropdownElement?.closeDropdown();
	};

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		if (this.#entityActionListElement) {
			this.#entityActionListElement.removeEventListener('action-executed', this.#onActionExecuted);
		}
	}

	#onDropdownClick(event: Event) {
		event.stopPropagation();
	}

	#onDropdownOpened() {
		if (this.#entityActionListElement) {
			this.#entityActionListElement.focus();
			return;
		}

		// First create dropdown content when the dropdown is opened.
		// Programmatically create the elements so they are cached if the dropdown is opened again
		// TODO: Clean up after a timeout. [NL]
		this.#entityActionListElement = new UmbEntityActionListElement();
		this.#entityActionListElement.addEventListener('action-executed', this.#onActionExecuted);
		this.#entityActionListElement.setAttribute('label', this.label ?? '');
		this._dropdownElement?.appendChild(this.#entityActionListElement);
	}

	override render() {
		return html`<umb-dropdown
			id="action-modal"
			@click=${this.#onDropdownClick}
			@opened=${this.#onDropdownOpened}
			@focusout=${(e: FocusEvent) => {
				const relatedTarget = e.relatedTarget as HTMLElement | null;
				if (!relatedTarget) return;
				const allowedTags = ['umb-entity-action-list', 'uui-scroll-container', 'uui-menu-item', 'umb-entity-action'];
				if (allowedTags.includes(relatedTarget.tagName.toLowerCase())) return;
				this.#onActionExecuted();
			}}
			.label=${this.label}
			look=${ifDefined(this.look)}
			?compact=${this.compact}
			hide-expand>
			<slot name="label" slot="label"></slot>
			<slot></slot>
		</umb-dropdown>`;
	}

	static override styles = [
		css`
			uui-scroll-container {
				max-height: 700px;
			}
		`,
	];
}

declare global {
	interface HTMLElementTagNameMap {
		'umb-entity-actions-dropdown': UmbEntityActionsDropdownElement;
	}
}
